/**
 * Form Field Transformation Utilities
 *
 * Converts API FormField types to app-friendly rendering types
 */

import type {
  FieldOption,
  FieldSetting,
  FieldType,
  FormField,
} from '@sunrise-ui/api/insights';

/**
 * Simple field option for component rendering
 */
export type SimpleFieldOption = {
  label: string;
  value: string;
};

/**
 * Configuration for rendering a single form field
 * This is the simplified/flattened structure used by UI components
 */
export type FormFieldRenderConfig = {
  id: string;
  type: FieldType;
  label: string;
  name?: string;
  placeholder?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  defaultValue?: string | number | boolean;
  options?: SimpleFieldOption[];
  // Text validation
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  // Number validation
  minValue?: number;
  maxValue?: number;
  // Date validation
  minDate?: Date;
  maxDate?: Date;
  // File settings
  acceptedFileTypes?: string[];
  maxFileSizeBytes?: number;
  allowMultiple?: boolean;
  // Select settings
  searchable?: boolean;
  maxSelections?: number;
  // Ordering
  orderIndex?: number;
  // Default checkout field indicator (for locked fields in form builder)
  isDefaultCheckoutField?: boolean;
};

/**
 * Parses the settings array from API FormField into flat props
 */
function parseFieldSettings(
  settings: FieldSetting[] | undefined,
): Partial<FormFieldRenderConfig> {
  const result: Partial<FormFieldRenderConfig> = {};

  if (!settings) return result;

  for (const setting of settings) {
    const settingType = setting.setting_type_name;

    // Extract the actual value from the nested structure
    const valueObj = setting.value as {
      value?: unknown;
      types?: string[];
    };

    const actualValue = valueObj.value;
    const types = valueObj.types;

    switch (settingType) {
      case 'required':
        result.required = actualValue as boolean;
        break;
      case 'placeholder':
        result.placeholder = actualValue as string;
        break;
      case 'default_value':
        result.defaultValue = actualValue as string;
        break;
      case 'min_length':
        result.minLength = actualValue as number;
        break;
      case 'max_length':
        result.maxLength = actualValue as number;
        break;
      case 'min_value':
        result.minValue = Number(actualValue);
        break;
      case 'max_value':
        result.maxValue = Number(actualValue);
        break;
      case 'pattern':
        result.pattern = actualValue as string;
        break;
      case 'disabled':
        result.disabled = actualValue as boolean;
        break;
      case 'readonly':
        result.readonly = actualValue as boolean;
        break;
      case 'searchable':
        result.searchable = actualValue as boolean;
        break;
      case 'max_selections':
        result.maxSelections = actualValue as number;
        break;
      case 'accepted_file_types':
        result.acceptedFileTypes = types;
        break;
      case 'max_file_size':
        result.maxFileSizeBytes = actualValue as number;
        break;
      case 'allow_multiple':
        result.allowMultiple = actualValue as boolean;
        break;
    }
  }

  return result;
}

/**
 * Converts API FieldOption array to simple options for rendering
 */
function parseFieldOptions(
  options: FieldOption[] | undefined,
): SimpleFieldOption[] {
  if (!options) return [];

  return options
    .filter((opt: FieldOption) => opt.is_active !== false)
    .sort((a: FieldOption, b: FieldOption) => a.order_index - b.order_index)
    .map((opt: FieldOption) => ({
      label: opt.label,
      value: opt.value,
    }));
}

/**
 * Converts API FormField to component render config
 */
function transformFormField(field: FormField): FormFieldRenderConfig {
  const settings = parseFieldSettings(field.settings);
  const options = parseFieldOptions(field.options);

  return {
    id: field.id ?? '',
    type: field.field_type as FieldType,
    label: field.label,
    name: field.name,
    helperText: field.help_text ?? undefined,
    orderIndex: field.order_index,
    options: options.length > 0 ? options : undefined,
    ...settings,
  };
}

/**
 * Converts array of API FormFields to render configs, sorted by order_index
 */
export function transformFormFields(
  fields: FormField[],
): FormFieldRenderConfig[] {
  return fields
    .filter((f) => f.is_active !== false)
    .sort((a, b) => a.order_index - b.order_index)
    .map(transformFormField);
}
